import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchClaimingProfiles,
  deleteClaimingProfiles,
} from '../services/claimingProfiles';
import ClaimProfileDetails from './ClaimingProfileDetails';
import { cat } from 'fontawesome';
import debounce from 'lodash.debounce';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ClaimingProfiles = () => {
  const { id } = useParams();
  const [profiles, setProfiles] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const navigate = useNavigate();

  const fetchProfiles = async () => {
    try {
      const response = await fetchClaimingProfiles(page, query);
      if (!response || !response.data) {
        alert('No profiles found');
        return;
      }
      setProfiles(response.data);
      setTotalPages(response.totalPages || 1);

      // Auto-select profile if ID is in route
      if (id) {
        const matched = response.data.find((profile) => profile._id === id);
        if (matched) {
          setSelectedProfile(matched);
        } else {
          alert('Profile ID not found on this page.');
        }
      }
    } catch (err) {
      alert('Failed to fetch profiles');
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, [page, query]);

  const handleSearch = () => {
    setPage(1);
    setQuery(search.trim());
  };

  useEffect(() => {
    const delayDebounce = debounce(() => {
      setPage(1);
      setQuery(search.trim());
    }, 300); // 300ms delay after typing stops

    delayDebounce();

    return () => delayDebounce.cancel();
  }, [search]);

  const handleDeleteField = async (id) => {
    try {
      const response = await deleteClaimingProfiles(id);
      if (response.message) {
        alert(response.message || 'Successfully deleted');
        fetchProfiles();
        return;
      }
      alert(response.error || 'Something went wrong');
    } catch (error) {
      alert(error.message || 'Something went wrong');
    }
  };

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 py-8">
        {!selectedProfile && (
          <>
            <div className="relative mb-6">
              {/* Mobile layout */}
              <div className="flex flex-col gap-2 sm:hidden">
                <button
                  onClick={() => navigate(-1)}
                  className="
                    inline-flex
                    items-center
                    gap-2
                    w-fit
                    px-4
                    py-2
                    rounded-md
                    bg-gray-200
                    text-gray-700
                    text-sm
                    font-medium
                    hover:bg-gray-300
                    transition
                  "
                >
                  <ArrowLeft size={16} />
                  Back
                </button>

                <h1 className="text-2xl font-semibold text-gray-800 text-center">
                  Claiming Profiles
                </h1>
              </div>

              {/* Desktop layout */}
              <div className="hidden sm:flex items-center justify-center relative">
                <button
                  onClick={() => navigate(-1)}
                  className="
                    absolute
                    left-0
                    inline-flex
                    items-center
                    gap-2
                    px-4
                    py-2
                    rounded-md
                    bg-gray-200
                    text-gray-700
                    text-sm
                    font-medium
                    hover:bg-gray-300
                    transition
                  "
                >
                  <ArrowLeft size={16} />
                  Back
                </button>

                <h1 className="text-3xl font-semibold text-gray-800">
                  Claiming Profiles
                </h1>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <input
                type="text"
                placeholder="Search by name, email, license..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />

              <button
                onClick={handleSearch}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition"
              >
                Search
              </button>

              <button
                onClick={() => setSelectedProfile({})}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-medium hover:bg-gray-300 transition"
              >
                + Add New Profile
              </button>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {profiles.map((profile, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                >
                  <div className="space-y-1">
                    <h2 className="text-lg font-semibold text-gray-900">
                      {profile.firstName} {profile.lastName}
                    </h2>

                    <p className="text-sm font text-gray-700">
                      <span className="font-semibold">Email:</span>{' '}
                      {profile.email || 'N/A'}
                    </p>

                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Mobile:</span>{' '}
                      {profile.mobile
                        ? profile.mobile.replace(/\D/g, '').slice(-10)
                        : 'N/A'}
                    </p>

                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">License:</span>{' '}
                      {profile.licenseNumber || 'N/A'}
                    </p>

                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Agency:</span>{' '}
                      {profile.agencyAffiliation || 'N/A'}
                    </p>

                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Location:</span>{' '}
                      {profile.primaryLocation || 'N/A'}
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-4 sm:justify-end">
                    <button
                      className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition"
                      onClick={() => setSelectedProfile(profile)}
                    >
                      View / Edit
                    </button>

                    <button
                      className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition"
                      onClick={() => handleDeleteField(profile._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-center items-center gap-4 mt-8">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg text-md font-medium hover:bg-blue-600 transition disabled:opacity-50"
              >
                Prev
              </button>

              <span className="text-md text-gray-700">
                Page {page} of {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg text-md font-medium hover:bg-blue-600 transition disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        )}

        {selectedProfile && (
          <ClaimProfileDetails
            profile={selectedProfile}
            onClose={() => setSelectedProfile(null)}
          />
        )}
      </div>
    </>
  );
};

export default ClaimingProfiles;
